<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useHead } from '@unhead/vue'
import { DEFAULT_LOCALE, type Locale } from '@/i18n'
import { staticPageHref, accountHref } from '@/router/products'
import { useAuth } from '@/composables/useAuth'
import { useDocuments } from '@/composables/useDocuments'
import { uploadDocument } from '@/services/account'
import { ApiError } from '@/services/http'
import type { DocumentCategory } from '@/data/seedMember'
import type { RawDocumentKind } from '@/types/api'
import AccountLayout from '@/components/layout/AccountLayout.vue'

const { t, locale } = useI18n()
const route = useRoute()
const router = useRouter()
const { member, reloadMember } = useAuth()

const currentLocale = computed<Locale>(() => (route.meta.locale as Locale) ?? DEFAULT_LOCALE)
const loginHref = computed(() => staticPageHref('login', currentLocale.value))
const contactHref = computed(() => accountHref('contact', currentLocale.value))
useHead({ title: computed(() => `${t('auth.account.documents.navTitle')} — MySmerra`) })

/** Accès réservé. */
onMounted(() => {
  if (!member.value) router.replace(loginHref.value)
})

const { documents } = useDocuments()

/* --- Onglets de filtre (Tous / Identité / Étude / Justificatif / Remboursement). --- */
const TABS = ['all', 'identite', 'etude', 'justificatif', 'remboursement'] as const
type Tab = (typeof TABS)[number]
const activeTab = ref<Tab>('all')
const counts = computed<Record<Tab, number>>(() => ({
  all: documents.value.length,
  identite: documents.value.filter((d) => d.category === 'identite').length,
  etude: documents.value.filter((d) => d.category === 'etude').length,
  justificatif: documents.value.filter((d) => d.category === 'justificatif').length,
  remboursement: documents.value.filter((d) => d.category === 'remboursement').length,
}))
const filtered = computed(() =>
  activeTab.value === 'all'
    ? documents.value
    : documents.value.filter((d) => d.category === (activeTab.value as DocumentCategory)),
)

/** Date ISO → format localisé court. */
function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString(locale.value, {
    day: '2-digit', month: '2-digit', year: 'numeric',
  })
}

/* --- Document upload (POST /documents, linked to the connected member). --- */

/** Selectable document types (labels under `documents.kinds.*`). */
const UPLOAD_KINDS: RawDocumentKind[] = [
  'carte_identite',
  'passeport',
  'visa_titre_sejour',
  'carte_etudiante',
  'justificatif_domicile',
  'attestation_securite_sociale',
  'rib_remboursement',
]
/** Accepted formats and max size — aligned with the backend validator and the UI copy. */
const ACCEPTED_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png']
const ACCEPT_ATTR = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png'
const MAX_SIZE = 10 * 1024 * 1024 // 10 Mo

const fileInput = ref<HTMLInputElement | null>(null)
const selectedKind = ref<RawDocumentKind | ''>('')
const uploading = ref(false)
const uploadError = ref<string | null>(null)
const uploadDone = ref(false)
const dragging = ref(false)

/** Opens the native file picker. */
function pickFile() {
  uploadError.value = null
  uploadDone.value = false
  if (!selectedKind.value) {
    uploadError.value = t('auth.account.documents.errors.kindRequired')
    return
  }
  fileInput.value?.click()
}

/** Backend error codes (POST /documents) mapped to their i18n message keys. */
const UPLOAD_ERROR_KEYS: Record<string, string> = {
  invalid_kind: 'auth.account.documents.errors.invalidKind',
  invalid_type: 'auth.account.documents.errors.invalidType',
  too_large: 'auth.account.documents.errors.tooLarge',
  document_required: 'auth.account.documents.errors.documentRequired',
}

/** Client-side check (type + size) before sending. Returns an i18n key or null. */
function validateFile(file: File): string | null {
  if (!ACCEPTED_TYPES.includes(file.type)) return 'auth.account.documents.errors.invalidType'
  if (file.size > MAX_SIZE) return 'auth.account.documents.errors.tooLarge'
  return null
}

/** Resolves the localized message from an upload ApiError's error codes. */
function uploadErrorMessage(err: ApiError): string {
  if (err.status === 0) return t('auth.account.documents.errors.network')
  const raw = (err.body as { errors?: unknown } | undefined)?.errors
  const codes = Array.isArray(raw) ? raw.map(String) : typeof raw === 'string' ? [raw] : []
  for (const code of codes) {
    const key = UPLOAD_ERROR_KEYS[code]
    if (key) return t(key)
  }
  return t('auth.account.documents.errors.generic')
}

/** Sends the file to the backend, then refreshes the document list. */
async function sendFile(file: File) {
  if (!selectedKind.value) {
    uploadError.value = t('auth.account.documents.errors.kindRequired')
    return
  }
  const invalid = validateFile(file)
  if (invalid) {
    uploadError.value = t(invalid)
    return
  }

  uploading.value = true
  uploadError.value = null
  uploadDone.value = false
  try {
    await uploadDocument(file, selectedKind.value)
    await reloadMember() // reload /me so the list reflects the new document
    uploadDone.value = true
    selectedKind.value = ''
  } catch (err) {
    uploadError.value =
      err instanceof ApiError ? uploadErrorMessage(err) : t('auth.account.documents.errors.generic')
  } finally {
    uploading.value = false
  }
}

/** Changement via l'input fichier. */
function onFileChange(e: Event) {
  const input = e.target as HTMLInputElement
  const file = input.files?.[0]
  if (file) void sendFile(file)
  input.value = '' // allows re-selecting the same file
}

/** Drag-and-drop onto the upload zone. */
function onDrop(e: DragEvent) {
  dragging.value = false
  uploadDone.value = false
  if (!selectedKind.value) {
    uploadError.value = t('auth.account.documents.errors.kindRequired')
    return
  }
  const file = e.dataTransfer?.files?.[0]
  if (file) void sendFile(file)
}
</script>

<template>
  <AccountLayout v-if="member" active="documents">
    <section class="doc">
      <!-- Fil d'ariane + titre -->
      <nav class="doc__crumb" aria-label="breadcrumb">
        <span>{{ t('auth.account.documents.breadcrumbHome') }}</span>
        <span class="doc__crumb-sep" aria-hidden="true">›</span>
        <span class="doc__crumb-current">{{ t('auth.account.documents.navTitle') }}</span>
      </nav>
      <header class="doc__head">
        <h1 class="doc__title">{{ t('auth.account.documents.navTitle') }}</h1>
        <p class="doc__subtitle">{{ t('auth.account.documents.subtitle') }}</p>
      </header>

      <div class="doc__grid">
        <!-- Colonne principale : onglets + tableau -->
        <div class="doc__main">
          <div class="tabs" role="tablist">
            <button
              v-for="tab in TABS"
              :key="tab"
              type="button"
              role="tab"
              class="tabs__btn"
              :class="{ 'is-active': activeTab === tab }"
              :aria-selected="activeTab === tab"
              @click="activeTab = tab"
            >
              {{ t('auth.account.documents.tabs.' + tab) }} ({{ counts[tab] }})
            </button>
          </div>

          <section class="table-card">
            <h2 class="table-card__title">{{ t('auth.account.documents.allTitle') }}</h2>
            <div class="table" role="table">
              <div class="table__head" role="row">
                <span role="columnheader">{{ t('auth.account.documents.colName') }}</span>
                <span role="columnheader">{{ t('auth.account.documents.colDate') }}</span>
                <span class="table__actions-h" role="columnheader">{{ t('auth.account.documents.colActions') }}</span>
              </div>

              <p v-if="!filtered.length" class="table__empty">{{ t('auth.account.documents.empty') }}</p>

              <div v-for="d in filtered" :key="d.fileName" class="drow" role="row">
                <div class="drow__name">
                  <span class="drow__icon" :class="`drow__icon--${d.category}`" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="20" height="20">
                      <template v-if="d.category === 'identite'">
                        <rect x="3" y="5" width="18" height="14" rx="2" fill="none" stroke="currentColor" stroke-width="1.6"/>
                        <circle cx="8.5" cy="11" r="2" fill="none" stroke="currentColor" stroke-width="1.5"/>
                        <path d="M13 10h5M13 13h5M5.5 15c.6-1.3 4.4-1.3 5 0" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                      </template>
                      <template v-else-if="d.category === 'etude'">
                        <path d="M12 4 2 9l10 5 8-4v6" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>
                        <path d="M6 11.5V16c0 1.1 2.7 2.5 6 2.5s6-1.4 6-2.5v-4.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>
                      </template>
                      <template v-else>
                        <path d="M7 3h7l4 4v14H7z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>
                        <path d="M14 3v4h4M9 13h6M9 16h6" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                      </template>
                    </svg>
                  </span>
                  <span class="drow__text">
                    <span class="drow__title">{{ d.title }}</span>
                    <span class="drow__file">{{ d.fileName }}</span>
                  </span>
                </div>
                <span class="drow__date">{{ fmtDate(d.addedAt) }}</span>
                <span class="drow__actions">
                  <a v-if="d.downloadUrl" :href="d.downloadUrl" class="iconbtn" :aria-label="t('auth.account.documents.download')" download>
                    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                      <path d="M12 4v10m0 0 4-4m-4 4-4-4" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M5 19h14" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>
                    </svg>
                  </a>
                  <a v-if="d.url" :href="d.url" class="iconbtn" target="_blank" rel="noopener" :aria-label="t('auth.account.documents.preview')">
                    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>
                      <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" stroke-width="1.6"/>
                    </svg>
                  </a>
                </span>
              </div>
            </div>
          </section>

          <!-- Document manquant -->
          <article class="nf">
            <span class="nf__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="22" height="22">
                <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" stroke-width="1.6"/>
                <path d="M16 16l5 5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
              </svg>
            </span>
            <div class="nf__body">
              <h3 class="nf__title">{{ t('auth.account.documents.notFoundTitle') }}</h3>
              <p class="nf__desc">{{ t('auth.account.documents.notFoundDesc') }}</p>
              <RouterLink class="nf__cta" :to="contactHref">{{ t('auth.account.documents.notFoundCta') }}</RouterLink>
            </div>
          </article>
        </div>

        <!-- Colonne latérale : upload + conseils -->
        <aside class="doc__aside">
          <section class="upload">
            <h2 class="upload__title">{{ t('auth.account.documents.addTitle') }}</h2>
            <p class="upload__desc">{{ t('auth.account.documents.addDesc') }}</p>

            <label class="upload__field">
              <span class="upload__label">{{ t('auth.account.documents.kindLabel') }}</span>
              <select v-model="selectedKind" class="upload__select" :disabled="uploading">
                <option value="" disabled>{{ t('auth.account.documents.kindPlaceholder') }}</option>
                <option v-for="k in UPLOAD_KINDS" :key="k" :value="k">
                  {{ t('auth.account.documents.kinds.' + k) }}
                </option>
              </select>
            </label>

            <div
              class="drop"
              :class="{ 'is-dragging': dragging, 'is-busy': uploading }"
              @dragover.prevent="dragging = true"
              @dragleave.prevent="dragging = false"
              @drop.prevent="onDrop"
            >
              <span class="drop__icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="28" height="28">
                  <path d="M6 18a4 4 0 0 1-.5-7.97A6 6 0 0 1 17 8.5a3.5 3.5 0 0 1 1 6.86" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M12 12v7m0-7 3 3m-3-3-3 3" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
              <p class="drop__hint">{{ t('auth.account.documents.dropHint') }}</p>
              <button type="button" class="btn-outline" :disabled="uploading" @click="pickFile">
                {{ uploading ? t('auth.account.documents.uploading') : t('auth.account.documents.chooseFile') }}
              </button>
              <p class="drop__formats">{{ t('auth.account.documents.formats') }}</p>
              <input
                ref="fileInput"
                type="file"
                class="upload__input"
                :accept="ACCEPT_ATTR"
                @change="onFileChange"
              />
            </div>

            <p v-if="uploadError" class="upload__msg upload__msg--error" role="alert">{{ uploadError }}</p>
            <p v-else-if="uploadDone" class="upload__msg upload__msg--ok" role="status">
              {{ t('auth.account.documents.success') }}
            </p>
          </section>

          <section class="tips">
            <h2 class="tips__title">{{ t('auth.account.documents.tipsTitle') }}</h2>
            <ul class="facts">
              <li class="fact">
                <span class="fact__icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="20" height="20">
                    <path d="M5 13l4 4 10-10" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </span>
                <div class="fact__body">
                  <span class="fact__title">{{ t('auth.account.documents.upToDateTitle') }}</span>
                  <span class="fact__desc">{{ t('auth.account.documents.upToDateDesc') }}</span>
                </div>
              </li>
              <li class="fact">
                <span class="fact__icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="20" height="20">
                    <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="1.6"/>
                    <path d="M12 11v5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                    <circle cx="12" cy="7.5" r="1" fill="currentColor"/>
                  </svg>
                </span>
                <div class="fact__body">
                  <span class="fact__title">{{ t('auth.account.documents.usageTitle') }}</span>
                  <span class="fact__desc">{{ t('auth.account.documents.usageDesc') }}</span>
                </div>
              </li>
            </ul>
          </section>
        </aside>
      </div>
    </section>
  </AccountLayout>
</template>

<style scoped>
.doc { display: flex; flex-direction: column; gap: 1rem; }

/* Fil d'ariane + titre */
.doc__crumb { display: flex; align-items: center; gap: 0.4rem; font-size: var(--text-sm); color: var(--text-muted); }
.doc__crumb-current { color: var(--color-chocolate); font-weight: 600; }
.doc__title { font-size: var(--text-xl); font-weight: 700; color: var(--color-chocolate); }
.doc__subtitle { margin-top: 0.15rem; font-size: var(--text-sm); color: var(--text-muted); }

/* Grille principale / latérale */
.doc__grid {
  display: grid;
  grid-template-columns: 1.7fr 1fr;
  gap: 1.25rem;
  align-items: start;
  margin-top: 0.25rem;
}
.doc__main { display: flex; flex-direction: column; gap: 1.25rem; }
.doc__aside { display: flex; flex-direction: column; gap: 1.25rem; }

/* Onglets */
.tabs { display: flex; flex-wrap: wrap; gap: 0.5rem; }
.tabs__btn {
  padding: 0.4rem 0.9rem;
  border: 1px solid var(--border);
  border-radius: var(--radius-pill);
  background: var(--color-white);
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--color-chocolate);
  cursor: pointer;
  transition: background var(--dur-fast), border-color var(--dur-fast), color var(--dur-fast);
}
.tabs__btn:hover { border-color: var(--primary); }
.tabs__btn.is-active { background: var(--color-chocolate); border-color: var(--color-chocolate); color: var(--color-white); }

/* Tableau des documents */
.table-card {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  background: var(--color-white);
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-sm);
  padding: 1.25rem;
}
.table-card__title { font-size: var(--text-lg); font-weight: 700; color: var(--color-chocolate); }
.table { display: flex; flex-direction: column; }
.table__head,
.drow {
  display: grid;
  grid-template-columns: 1fr 8rem 5rem;
  align-items: center;
  gap: 0.75rem;
}
.table__head {
  padding: 0 0.25rem 0.6rem;
  border-bottom: 1px solid var(--border);
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--text-muted);
}
.table__actions-h { text-align: right; }
.table__empty { padding: 1rem 0.25rem; font-size: var(--text-sm); color: var(--text-muted); }
.drow { padding: 0.75rem 0.25rem; border-bottom: 1px solid var(--border); }
.drow:last-child { border-bottom: 0; }
.drow__name { display: flex; align-items: center; gap: 0.7rem; min-width: 0; }
.drow__icon {
  display: grid;
  place-items: center;
  width: 2.25rem;
  height: 2.25rem;
  flex-shrink: 0;
  border-radius: var(--radius-md);
  color: var(--primary);
  background: color-mix(in oklab, var(--primary) 10%, white);
}
.drow__icon--etude { color: var(--color-cherry); background: color-mix(in oklab, var(--color-cherry) 10%, white); }
.drow__text { display: flex; flex-direction: column; gap: 0.1rem; min-width: 0; }
.drow__title { font-size: var(--text-sm); font-weight: 600; color: var(--color-chocolate); }
.drow__file { font-size: var(--text-xs); color: var(--text-muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.drow__date { font-size: var(--text-sm); color: var(--color-chocolate); }
.drow__actions { display: flex; justify-content: flex-end; gap: 0.35rem; }
.iconbtn {
  display: grid;
  place-items: center;
  width: 2rem;
  height: 2rem;
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  background: var(--color-white);
  color: var(--color-chocolate);
  cursor: pointer;
  transition: border-color var(--dur-fast), color var(--dur-fast);
}
.iconbtn:hover { border-color: var(--primary); color: var(--primary); }

/* Document manquant */
.nf {
  display: flex;
  align-items: flex-start;
  gap: 0.8rem;
  padding: 1rem;
  border: 1px dashed var(--border-strong);
  border-radius: var(--radius-lg);
  background: var(--bg-soft);
}
.nf__icon {
  display: grid;
  place-items: center;
  width: 2.5rem;
  height: 2.5rem;
  flex-shrink: 0;
  border-radius: var(--radius-lg);
  color: var(--text-muted);
  background: var(--color-white);
}
.nf__title { font-size: var(--text-sm); font-weight: 700; color: var(--color-chocolate); }
.nf__desc { margin-top: 0.15rem; font-size: var(--text-xs); color: var(--text-muted); line-height: var(--leading-normal); }
.nf__cta {
  display: inline-block;
  margin-top: 0.3rem;
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--primary);
  text-decoration: none;
}
.nf__cta:hover { color: var(--color-cherry); }

/* Upload */
.upload,
.tips {
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
  background: var(--color-white);
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-sm);
  padding: 1.25rem;
}
.upload__title,
.tips__title { font-size: var(--text-lg); font-weight: 700; color: var(--color-chocolate); }
.upload__desc { font-size: var(--text-sm); color: var(--text-muted); }
.drop {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.6rem;
  text-align: center;
  padding: 1.25rem 1rem;
  border: 1.5px dashed var(--border-strong);
  border-radius: var(--radius-lg);
  background: var(--bg-soft);
}
.drop__icon { color: var(--primary); }
.drop__hint { font-size: var(--text-sm); font-weight: 600; color: var(--color-chocolate); }
.drop__formats { font-size: var(--text-xs); color: var(--text-muted); line-height: var(--leading-normal); }
.btn-outline {
  padding: 0.55rem 1.2rem;
  border: 1.5px solid var(--primary);
  border-radius: var(--radius-pill);
  background: var(--color-white);
  color: var(--primary);
  font-size: var(--text-sm);
  font-weight: 600;
  cursor: pointer;
  transition: background var(--dur-fast) var(--ease-out);
}
.btn-outline:hover { background: color-mix(in oklab, var(--primary) 8%, white); }
.btn-outline:disabled { opacity: 0.6; cursor: default; }

/* Champ « type de document » */
.upload__field { display: flex; flex-direction: column; gap: 0.35rem; }
.upload__label { font-size: var(--text-sm); font-weight: 600; color: var(--color-chocolate); }
.upload__select {
  padding: 0.55rem 0.7rem;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-md);
  background: var(--color-white);
  font-size: var(--text-sm);
  color: var(--color-chocolate);
  cursor: pointer;
}
.upload__select:focus-visible { outline: 2px solid var(--primary); outline-offset: 1px; }
.upload__input { display: none; }

/* Drop-zone states */
.drop.is-dragging { border-color: var(--primary); background: color-mix(in oklab, var(--primary) 8%, white); }
.drop.is-busy { opacity: 0.7; }

/* Outcome message (error / success) */
.upload__msg { font-size: var(--text-sm); font-weight: 600; line-height: var(--leading-normal); }
.upload__msg--error { color: var(--color-cherry); }
.upload__msg--ok { color: var(--primary); }

/* Conseils (facts) */
.facts { display: flex; flex-direction: column; gap: 0.25rem; margin: 0; padding: 0; list-style: none; }
.fact { display: flex; align-items: flex-start; gap: 0.7rem; padding: 0.5rem 0; }
.fact + .fact { border-top: 1px solid var(--border); }
.fact__icon { color: var(--primary); flex-shrink: 0; margin-top: 0.1rem; }
.fact__body { display: flex; flex-direction: column; gap: 0.1rem; min-width: 0; }
.fact__title { font-size: var(--text-sm); font-weight: 700; color: var(--color-chocolate); }
.fact__desc { font-size: var(--text-xs); color: var(--text-muted); line-height: var(--leading-normal); }

@media (max-width: 64rem) {
  .doc__grid { grid-template-columns: 1fr; }
}
@media (max-width: 40rem) {
  .table__head,
  .drow { grid-template-columns: 1fr auto; }
  .table__head span:nth-child(2),
  .drow__date { display: none; }
}
</style>
